import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import policyService from '../../services/policyService';

const CACHE_DURATION = 10 * 60 * 1000; // 10 minutes

// Fetch authentication risk data
export const fetchAuthRisk = createAsyncThunk(
  'dashboard/fetchAuthRisk',
  async (_, { getState, rejectWithValue }) => {
    try {
      const { authRisk } = getState().dashboard;
      const now = Date.now();

      if (authRisk.lastFetched && now - authRisk.lastFetched < CACHE_DURATION) {
        return { cached: true };
      }

      const response = await policyService.getAuthenticationRisk();
      // console.log("authrisk",response.data)
      return response.data;
    } catch (error) {
      // console.log("error",error.response.data.error)
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  }
);

// Fetch attack vectors based on type
export const fetchAttackVectors = createAsyncThunk(
  'dashboard/fetchAttackVectors',
  async (tabType, { getState, rejectWithValue }) => {
    try {
      const { attackVectors } = getState().dashboard;
      const now = Date.now();
      const cached = attackVectors[tabType];

      if (cached?.lastFetched && now - cached.lastFetched < CACHE_DURATION) {
        return { cached: true, tabType };
      }

      const response = await policyService.getTopAttackVectors(tabType);

      return { data: response?.data?.vectors || [], tabType };
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Fetch authentication risk vector users
export const fetchAuthRiskVectorUsers = createAsyncThunk(
  'dashboard/fetchAuthRiskVectorUsers',
  async (metricName, { getState, rejectWithValue }) => {
    try {
      const { authRiskVectorUsers } = getState().dashboard;
      const now = Date.now();
      const cached = authRiskVectorUsers.cache[metricName];

      // Check if we have cached data for this specific metric
      if (cached?.lastFetched && now - cached.lastFetched < CACHE_DURATION) {
        return { cached: true, metricName, data: cached.data };
      }

      const response = await policyService.getAuthRiskVectorUsers(metricName);

      // Check if response contains an error
      if (response?.error) {
        return rejectWithValue({
          message: response.error,
          type: 'api_error'
        });
      }

      return { metricName, data: response?.data };
    } catch (error) {
      // Handle different error types
      const errorMessage = error.response?.data?.error ||
        error.response?.data?.message ||
        error.message ||
        'An unexpected error occurred';

      return rejectWithValue({
        message: errorMessage,
        type: error.response?.data?.error ? 'api_error' : 'network_error',
        statusCode: error.response?.status
      });
    }
  }
);

export const fetchMfaActivity = createAsyncThunk(
  'dashboard/fetchMfaActivity',
  async (timePeriod = '24h', { getState, rejectWithValue }) => {
    try {
      const { mfaActivity } = getState().dashboard;
      const now = Date.now();
      const cached = mfaActivity.cache?.[timePeriod];

      // Check if we have fresh cached data for this SPECIFIC timeframe
      if (cached?.lastFetched && now - cached.lastFetched < CACHE_DURATION) {
        return { cached: true, data: cached.data, timePeriod };
      }

      const response = await policyService.getMfaActivity(timePeriod);
      return { data: response, timePeriod };
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);


// export const fetchVectorIdentities = createAsyncThunk(
//   'dashboard/fetchVectorIdentities',
//   async ({ vectorId,totalIdentities }, { getState, rejectWithValue }) => {
//     // console.log("vector with total identities",vectorId,totalIdentities)
//     try {
//       const { vectorIdentities } = getState().dashboard;
//       const cached = vectorIdentities.cache[vectorId, totalIdentities];
//       const now = Date.now();

//       // If cached data exists and is fresh (within 10 mins), return it
//       if (cached?.lastFetched && now - cached.lastFetched < CACHE_DURATION) {
//         return cached.data;
//       }

//       const response = await policyService.getAttackVectorIdentities(vectorId,totalIdentities);
//       // console.log("attack-vector-response",response.data.identities)
//       return response?.data?.identities || []; 
//     } catch (error) {
//       return rejectWithValue(error.response?.data || error.message);
//     }
//   }
// );
export const fetchVectorIdentities = createAsyncThunk(
  'dashboard/fetchVectorIdentities',
  async ({ vectorId, page = 1, limit = 10, search }, { getState, rejectWithValue }) => {
    try {
      // Note: We usually don't cache paginated results easily by ID alone, 
      // but you can include page/limit in the cache key if needed.
      const response = await policyService.getAttackVectorIdentities(vectorId, page, limit, search);
      // console.log("response",response.data);
      return {
        identities: response?.data?.identities || [],
        totalCount: response?.data?.total || 0, // Ensure your API returns this
        page,
      };
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

const dashboardSlice = createSlice({
  name: 'dashboard',
  initialState: {
    authRisk: {
      data: null,
      loading: false,
      error: null,
      lastFetched: null,
    },
    mfaActivity: {
      data: null,
      loading: false,
      error: null,
      cache: {},
    },
    attackVectors: {
      human: {
        data: [],
        totalCount: 0,
        loading: false,
        error: null,
        lastFetched: null,
      },
      'non-human': {
        data: [],
        loading: false,
        error: null,
        lastFetched: null,
      },
    },
    authRiskVectorUsers: {
      data: null,
      loading: false,
      error: null,
      cache: {}, // Store data per metric_name
    },
    vectorIdentities: {
      data: [],
      loading: false,
      error: null,
      cache: {},      // Store data per vectorId: { [vectorId]: { data: [], lastFetched: timestamp } }
    },
  },
  reducers: {
    clearDashboardCache: (state) => {
      state.authRisk.lastFetched = null;
      state.attackVectors.human.lastFetched = null;
      state.attackVectors['non-human'].lastFetched = null;
      state.authRiskVectorUsers.cache = {};
    },
    clearMfaActivityCache: (state) => {
      state.mfaActivity.cache = {};
    },
    invalidateAuthRisk: (state) => {
      state.authRisk.lastFetched = null;
    },
    invalidateAttackVectors: (state, action) => {
      if (action.payload) {
        state.attackVectors[action.payload].lastFetched = null;
      } else {
        state.attackVectors.human.lastFetched = null;
        state.attackVectors['non-human'].lastFetched = null;
      }
    },
    invalidateAuthRiskVectorUsers: (state, action) => {
      if (action.payload) {
        // Invalidate specific metric
        delete state.authRiskVectorUsers.cache[action.payload];
      } else {
        // Invalidate all
        state.authRiskVectorUsers.cache = {};
      }
    },
    clearAuthRiskVectorUsers: (state) => {
      state.authRiskVectorUsers.data = null;
      state.authRiskVectorUsers.error = null;
    },
    clearVectorIdentities: (state) => {
      state.vectorIdentities.data = [];
      state.vectorIdentities.totalCount = 0;
      state.vectorIdentities.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Auth Risk
      .addCase(fetchAuthRisk.pending, (state) => {
        state.authRisk.loading = true;
        state.authRisk.error = null;
      })
      .addCase(fetchAuthRisk.fulfilled, (state, action) => {
        state.authRisk.loading = false;
        if (!action.payload.cached) {
          state.authRisk.data = action.payload;
          state.authRisk.lastFetched = Date.now();
        }
      })
      .addCase(fetchAuthRisk.rejected, (state, action) => {
        state.authRisk.loading = false;
        state.authRisk.error = action.payload;
      })
      // Attack Vectors
      .addCase(fetchAttackVectors.pending, (state, action) => {
        const tabType = action.meta.arg;
        state.attackVectors[tabType].loading = true;
        state.attackVectors[tabType].error = null;
      })
      .addCase(fetchAttackVectors.fulfilled, (state, action) => {
        const { tabType, cached, data } = action.payload;
        state.attackVectors[tabType].loading = false;
        if (!cached) {
          state.attackVectors[tabType].data = data;
          state.attackVectors[tabType].lastFetched = Date.now();
        }
      })
      .addCase(fetchAttackVectors.rejected, (state, action) => {
        const tabType = action.meta.arg;
        state.attackVectors[tabType].loading = false;
        state.attackVectors[tabType].error = action.payload;
      })
      //mfaActivity
      .addCase(fetchMfaActivity.pending, (state) => {
        state.mfaActivity.loading = true;
        state.mfaActivity.error = null;
      })
      .addCase(fetchMfaActivity.fulfilled, (state, action) => {
        state.mfaActivity.loading = false;
        const { cached, data, timePeriod } = action.payload;

        if (!cached) {
          // Store raw fetched response inside timeframe cache
          state.mfaActivity.cache[timePeriod] = {
            data: data,
            lastFetched: Date.now()
          };
          state.mfaActivity.data = data;
        } else {
          // Load timeframe data from active cache
          state.mfaActivity.data = data;
        }
      })
      .addCase(fetchMfaActivity.rejected, (state, action) => {
        state.mfaActivity.loading = false;
        state.mfaActivity.error = action.payload;
      })
      // Auth Risk Vector Users
      .addCase(fetchAuthRiskVectorUsers.pending, (state) => {
        state.authRiskVectorUsers.loading = true;
        state.authRiskVectorUsers.error = null;
      })
      .addCase(fetchAuthRiskVectorUsers.fulfilled, (state, action) => {
        const { metricName, data, cached } = action.payload;
        state.authRiskVectorUsers.loading = false;

        if (!cached) {
          // Cache the data for this specific metric
          state.authRiskVectorUsers.cache[metricName] = {
            data,
            lastFetched: Date.now(),
          };
        }

        // Set current data
        state.authRiskVectorUsers.data = data;
      })
      .addCase(fetchAuthRiskVectorUsers.rejected, (state, action) => {
        state.authRiskVectorUsers.loading = false;
        state.authRiskVectorUsers.error = action.payload;
      })
      .addCase(fetchVectorIdentities.pending, (state) => {
        state.vectorIdentities.loading = true;
        state.vectorIdentities.error = null;
      })
      .addCase(fetchVectorIdentities.fulfilled, (state, action) => {
        state.vectorIdentities.loading = false;
        state.vectorIdentities.data = action.payload.identities;
        state.vectorIdentities.totalCount = action.payload.totalCount;
      })
      .addCase(fetchVectorIdentities.rejected, (state, action) => {
        state.vectorIdentities.loading = false;
        state.vectorIdentities.error = action.payload;
      });
  },
});

export const {
  clearDashboardCache,
  invalidateAuthRisk,
  invalidateAttackVectors,
  invalidateAuthRiskVectorUsers,
  clearAuthRiskVectorUsers,
  clearVectorIdentities,
  clearMfaActivityCache
} = dashboardSlice.actions;

export default dashboardSlice.reducer;